import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { regexAnyCharAtLeastOneNumberAndLetter } from '@constants/regex';
import { styled } from '@mui/system';
import Paper from '@mui/material/Paper';
import { authService } from '@services';
import { Input } from '@components/Input';
import Button from '@components/Button';
import { AuthLayout } from '@features/AuthLayout';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { Typography } from '@components/Typography';
import { alertActions } from '@actions';
import { handleApiError } from '@helpers/api';
import { RoutePath } from '@constants/routes';

const Form = styled(Paper)({
    marginBottom: '24px',
    padding: '24px',
    position: 'relative',
    width: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    '@media (max-width: 600px)': {
        padding: '16px',
        width: '100%',
    },
});

const Overlay = styled('div')({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255,255,255,0.6)',
    zIndex: 2,
});

const FormSuccess = styled(Form)({
    padding: '24px',
    width: '400px',
    height: '176px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    '@media (max-width: 600px)': {
        padding: '16px',
        width: '100%',
    },
});

const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [isSubmitSuccess, setSubmitSuccess] = useState(false);

    const { token } = useParams();
    const alert = useSelector(state => state.alert.data);
    const dispatch = useDispatch();

    const ResetPasswordSchema = yup.object().shape({
        password: yup
            .string()
            .min(8, 'validation.minPasswordLength')
            .matches(regexAnyCharAtLeastOneNumberAndLetter, 'validation.wrongPasswordFormat')
            .required('validation.required'),
        confirmPassword: yup
            .string()
            .min(8, 'validation.minPasswordLength')
            .matches(regexAnyCharAtLeastOneNumberAndLetter, 'validation.wrongPasswordFormat')
            .required('validation.required')
            .sameAs('password', 'validation.confirmPasswordMatch'),
    });

    const resolver = useYupValidationResolver(ResetPasswordSchema);

    const {
        getValues,
        handleSubmit,
        control,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver: resolver,
        defaultValues: {
            password: '',
            confirmPassword: '',
        },
    });

    useEffect(() => {
        if (token) {
            getEmailByToken();
        } else {
            navigate(RoutePath.Error);
        }
    }, []);

    const getEmailByToken = async () => {
        try {
            const response = await authService.getEmailByToken(token);
            if (response.message === 'Invalid token') {
                navigate(RoutePath.Login);
            }
        } catch (_) {
            handleApiError(_);
        }
    };

    const onSubmit = async () => {
        const values = getValues();
        setSubmitSuccess(false);
        try {
            await authService.resetPassword(values.password, token);
            setSubmitSuccess(true);
        } catch (_) {
            dispatch(alertActions.error(alert[0]?.message || t('apiErrors.something_wrong')));
        }
    };

    return (
        <AuthLayout showLogo={false}>
            {isSubmitSuccess ? (
                <FormSuccess elevation={24}>
                    <Typography variant="h5" sx={{ marginBottom: '8px', color: 'rgba(0, 0, 0, .87)' }}>
                        {t('ResetPasswordPage.titleChanged')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                        {t('ResetPasswordPage.descriptionChanged')}
                    </Typography>
                    <Button type="button" variant="contained" fullWidth onClick={() => navigate(RoutePath.Login)}>
                        {t('buttons.logIn')}
                    </Button>
                </FormSuccess>
            ) : (
                <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Form elevation={24}>
                        {isSubmitting && <Overlay />}
                        <Typography variant="h5" sx={{ marginBottom: '8px', color: 'rgba(0, 0, 0, .87)' }}>
                            {t('ResetPasswordPage.titleReset')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
                            {t('ResetPasswordPage.descriptionReset')}
                        </Typography>
                        <Controller
                            control={control}
                            name="password"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field }}
                                    type="password"
                                    label={t('common.password')}
                                    sx={{ marginBottom: '24px' }}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="confirmPassword"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field }}
                                    type="password"
                                    label={t('ResetPasswordPage.confirmPassword')}
                                    fullWidth
                                    sx={{ marginBottom: '24px' }}
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            variant="contained"
                            fullWidth
                            sx={{ marginBottom: '16px' }}
                            disabled={isSubmitting || !isValid || !isDirty}
                        >
                            {t('buttons.changePassword')}
                        </Button>
                    </Form>
                </div>
            )}
        </AuthLayout>
    );
};

export default ResetPasswordPage;
